import type { ReactNode } from 'react'
import { useRef, useState, useEffect } from 'react'

import './style.scss'
import { IconMinusFilter, IconPlusFilter } from './utils/icons'
import type { FacetValue } from '../types'

type Props = {
  title: string
  index: string | number
  onAccordionChange?: () => void
  children: ReactNode
  onCloseTab?: () => void
  selectedValues?: FacetValue[]
  subTitle?: string | null
}

export function Accordion({
  title,
  index,
  onAccordionChange,
  onCloseTab,
  children,
  selectedValues,
  subTitle = null,
}: Readonly<Props>) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const [height, setHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  const toggleAccordion = () => {
    setIsAccordionOpen(!isAccordionOpen)
    onAccordionChange?.()
    if (isAccordionOpen) {
      onCloseTab?.()
    }
  }

  useEffect(() => {
    if (!contentRef.current) {
      return
    }

    setHeight(isAccordionOpen ? contentRef.current.scrollHeight : 0)
  }, [isAccordionOpen, children])

  return (
    <div
      className="accordion-item border-b border-gray py-5 last:border-b-none"
      data-accordion-item
      data-testid="filter-accordion-item"
      data-fs-facets-accordion-item
    >
      <button
        className="header-tab-accordion-filter flex justify-between items-center w-full"
        onClick={toggleAccordion}
        tabIndex={Number(index)}
        aria-expanded={isAccordionOpen}
        aria-controls={`accordion-content-${index}`}
        data-accordion-button
        data-testid="filter-accordion-button"
      >
        <h3 className="flex items-center">
          <div className="flex flex-col gap-2">
            <span className="text-base text-restructure-primary font-medium font-inter text-left leading-[21px]">
              {title}
            </span>
            {subTitle
              ? !isAccordionOpen && (
                  <small className="text-xs text-tertiary font-normal font-inter text-left">
                    {subTitle}
                  </small>
                )
              : selectedValues &&
                !isAccordionOpen && (
                  <small className="text-xs text-tertiary font-normal font-inter text-left empty:hidden">
                    {selectedValues.map((value) => value.label).join(', ')}
                  </small>
                )}
          </div>
        </h3>
        {isAccordionOpen ? <IconMinusFilter /> : <IconPlusFilter />}
      </button>

      <div
        id={`accordion-content-${index}`}
        className="accordion-content"
        ref={contentRef}
        role="region"
        style={{
          maxHeight: `${height}px`,
          transition: 'max-height 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  )
}
